/**
 * RailSearch — the shared rail search slot (DEV-1476, POLICY_NAVIGATION_RAILS §3).
 *
 * The Rail primitive had no search slot, so two rails rolled their own: the My
 * Impact rail (CommunitySwitcherRail's `.railSearch*`) and the Action Center
 * inbox (`.railSearch` / `.railSearchInput`). This is the one implementation.
 *
 * It encodes the §7.6-ratified affordance — visible magnifier, a clear border,
 * and a focus state that does NOT compete with the active lens tab. The
 * at-rest background is deliberately NEUTRAL: an earlier draft wore the
 * active-tab blue wash and read as "selected"/another tab. Blue arrives only
 * on focus.
 *
 * CHROME ONLY. What the query filters stays with the surface — this component
 * owns no filtering, no state, and no placeholder text (placeholders are
 * per-surface and already localized by their adopters).
 *
 * Collapse: it hides itself when it sits inside a collapsed Rail, keyed off the
 * primitive's `.l4e-rail[data-collapsed='true']` marker — the same mechanism
 * the collapsed label uses. Adopters therefore do NOT need to guard it behind
 * their own `!collapsed &&` check; rendering it unconditionally is correct.
 */
import React from 'react';
import styles from './RailSearch.module.css';

export interface RailSearchProps {
  value: string;
  onChange: (next: string) => void;
  /** Already-translated placeholder — per-surface, scoped to what it filters. */
  placeholder: string;
  /** Defaults to the placeholder when omitted (both are already translated). */
  ariaLabel?: string;
  className?: string;
}

export default function RailSearch({
  value,
  onChange,
  placeholder,
  ariaLabel,
  className,
}: RailSearchProps) {
  return (
    <div className={[styles.wrap, className || ''].filter(Boolean).join(' ')}>
      <svg
        className={styles.icon}
        width="13"
        height="13"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2.4" />
        <line
          x1="16.5"
          y1="16.5"
          x2="21"
          y2="21"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
        />
      </svg>
      <input
        type="text"
        className={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel || placeholder}
      />
    </div>
  );
}
